import { useState, useEffect, useRef } from 'react';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase';
import { createLogger } from '../utils/logger';

const logger = createLogger('useCheckoutCatalog');

/**
 * Load supermarket metadata plus product/inventory details for checkout pricing UI.
 * Uses refs for in-flight/fetched IDs so cart product list changes do not thrash fetches.
 * Inventory cache keys are `${supermarketId}:${productId}` so switching stores cannot
 * reuse stale deal/stock data from a previous supermarket on the same mount.
 */
export function useCheckoutCatalog({ supermarketId, cartInstance }) {
  const [supermarket, setSupermarket] = useState(null);
  const [productImages, setProductImages] = useState({});
  const [productDetails, setProductDetails] = useState({});
  const [inventoryDetails, setInventoryDetails] = useState({});

  const fetchedProductIdsRef = useRef(new Set());
  const fetchedInventoryIdsRef = useRef(new Set());
  const inventorySupermarketIdRef = useRef(supermarketId);

  useEffect(() => {
    let cancelled = false;

    const fetchSupermarket = async () => {
      if (!supermarketId) return;
      try {
        const supermarketDoc = await getDoc(doc(db, 'Supermarkets', supermarketId));
        if (cancelled) return;
        if (supermarketDoc.exists()) {
          const supermarketData = { id: supermarketDoc.id, ...supermarketDoc.data() };
          setSupermarket(supermarketData);
          logger.debug('[useCheckoutCatalog] Supermarket data loaded:', {
            id: supermarketDoc.id,
            name: supermarketData.display_name,
            full_payment_enabled: supermarketData.full_payment_enabled,
            delivery_enabled: supermarketData.delivery_enabled,
          });
        } else {
          logger.warn('[useCheckoutCatalog] Supermarket not found:', supermarketId);
          setSupermarket(null);
        }
      } catch (error) {
        logger.error('[useCheckoutCatalog] Error fetching supermarket:', error);
        if (!cancelled) setSupermarket(null);
      }
    };

    fetchSupermarket();
    return () => {
      cancelled = true;
    };
  }, [supermarketId]);

  // Reset supermarket-scoped inventory cache when the route supermarket changes.
  useEffect(() => {
    if (inventorySupermarketIdRef.current === supermarketId) return;
    inventorySupermarketIdRef.current = supermarketId;
    fetchedInventoryIdsRef.current = new Set();
    setInventoryDetails({});
  }, [supermarketId]);

  useEffect(() => {
    if (!cartInstance?.products || !supermarketId) return;

    let cancelled = false;

    const fetchProductDetails = async (productId) => {
      if (fetchedProductIdsRef.current.has(productId)) return;
      fetchedProductIdsRef.current.add(productId);

      try {
        const productDoc = await getDoc(doc(db, 'Products', productId));
        // Strict Mode / unmount can cancel mid-flight — free the cache slot so remount refetches.
        if (cancelled) {
          fetchedProductIdsRef.current.delete(productId);
          return;
        }
        if (!productDoc.exists()) return;

        const productData = productDoc.data();
        setProductDetails((prev) => ({ ...prev, [productId]: productData }));

        if (productData.media?.[0]?.url) {
          try {
            const storage = getStorage();
            const imageRef = ref(storage, productData.media[0].url);
            const imageUrl = await getDownloadURL(imageRef);
            if (!cancelled) {
              setProductImages((prev) => ({ ...prev, [productId]: imageUrl }));
            }
          } catch (imageError) {
            logger.warn(`Failed to load image for product ${productId}:`, imageError);
          }
        }
      } catch (error) {
        fetchedProductIdsRef.current.delete(productId);
        logger.error(`Failed to fetch product details for ${productId}:`, error);
      }
    };

    const fetchInventoryDetails = async (productId) => {
      const cacheKey = `${supermarketId}:${productId}`;
      if (fetchedInventoryIdsRef.current.has(cacheKey)) return;
      fetchedInventoryIdsRef.current.add(cacheKey);

      try {
        const inventoryQuery = query(
          collection(db, 'Inventory'),
          where('product_id', '==', productId),
          where('supermarket_id', '==', supermarketId),
          limit(1)
        );
        const inventorySnapshot = await getDocs(inventoryQuery);
        if (cancelled) {
          fetchedInventoryIdsRef.current.delete(cacheKey);
          return;
        }
        if (inventorySnapshot.empty) return;
        const inventoryData = inventorySnapshot.docs[0].data();
        setInventoryDetails((prev) => ({ ...prev, [productId]: inventoryData }));
      } catch (error) {
        fetchedInventoryIdsRef.current.delete(cacheKey);
        logger.error(`Failed to fetch inventory details for ${productId}:`, error);
      }
    };

    cartInstance.products.forEach((product) => {
      if (product.productId) {
        fetchProductDetails(product.productId);
        fetchInventoryDetails(product.productId);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [cartInstance, supermarketId]);

  return {
    supermarket,
    productImages,
    productDetails,
    inventoryDetails,
  };
}
